import { useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQueryParamsContext } from './useGlobalContext/useQueryParamsContext'

export const useQueryParams = (searchQueryParamsProp?: URLSearchParams) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { searchQueryParams: searchQueryParamsContext } =
    useQueryParamsContext()

  const searchQueryParams = searchQueryParamsProp || searchQueryParamsContext

  /** Get matching location.search value. */
  const getQueryParam = useCallback(
    (param: string): string => {
      if (!searchQueryParams) return ''
      const queryParamValue = searchQueryParams.get(param)
      if (!!queryParamValue) {
        return queryParamValue
      }

      return ''
    },
    [searchQueryParams]
  )

  /** Checks whether the query param exists in the location.search, even if the value is empty. */
  const hasQueryParam = useCallback(
    (param: string): boolean => {
      if (!searchQueryParams) return false
      const queryParamValue = searchQueryParams.get(param)
      if (queryParamValue !== null) {
        return true
      }

      return false
    },
    [searchQueryParams]
  )

  /** Delete matching location.search value. */
  const deleteQueryParam = useCallback(
    (paramToRemove: string | string[]) => {
      if (!searchQueryParams) return
      // typeof Array === 'object'
      if (typeof paramToRemove === 'object') {
        paramToRemove.forEach((param) => {
          searchQueryParams.delete(param)
        })
      } else {
        searchQueryParams.delete(paramToRemove)
      }

      navigate(
        {
          pathname: location.pathname,
          search: searchQueryParams.toString()
        },
        { replace: true }
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchQueryParams, location.pathname]
  )

  return {
    getQueryParam,
    hasQueryParam,
    deleteQueryParam
  }
}
